import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { simulateViewingConditions } from "../services/api";
import axios from "axios";
import { RootState } from "./store";
import { SimulationParams, SavedSimulation, WeatherPreferences } from "../types/types";

// Define interfaces for satellite types used within this slice
interface Satellite {
  id: string | number;
  name: string;
  viewingScore?: number;
  [key: string]: any; // Allow for other properties
}

// Define our own complete simulation result interface to match implementation
export interface ExtendedSimulationResult {
  id?: string;
  savedAt?: string;
  timestamp: string;
  location?: {
    name?: string;
    latitude?: number;
    longitude?: number;
    [key: string]: any;
  };
  overallScore?: number;
  satellites?: Satellite[];
  predicted_conditions?: {
    viewing_score: number;
    condition: string;
    [key: string]: any;
  };
  simulatedPasses?: Array<{
    name: string;
    time: string;
    [key: string]: any;
  }>;
  weather?: {
    temperature?: number;
    cloudCover?: number;
    [key: string]: any;
  };
  meta?: {
    dataSources?: string[];
    predictionAccuracy?: number;
    [key: string]: any;
  };
  recommendations?: string[];
  [key: string]: any; // Allow for other properties
}

interface SimulationState {
  result: ExtendedSimulationResult | null;
  savedSimulations: SavedSimulation[];
  selectedSavedSimulation: ExtendedSimulationResult | null;
  loading: boolean;
  error: string | null;
  savingSimulation: boolean;
  savingError: string | null;
  loadingSavedList: boolean;
  loadingSavedListError: string | null;
  loadingSavedSimulation: boolean;
  loadingSavedSimulationError: string | null;
  lastUpdated: string | null;
}

const initialState: SimulationState = {
  result: null,
  savedSimulations: [],
  selectedSavedSimulation: null,
  loading: false,
  error: null,
  savingSimulation: false,
  savingError: null,
  loadingSavedList: false,
  loadingSavedListError: null,
  loadingSavedSimulation: false,
  loadingSavedSimulationError: null,
  lastUpdated: null,
};

// Updated simulateConditions to use SimulationParams from types
export const simulateConditions = createAsyncThunk<
  ExtendedSimulationResult,
  SimulationParams,
  { state: RootState; rejectValue: string }
>(
  "simulation/simulateConditions",
  async (params: SimulationParams, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const { lat, lon, time, locationName, duration } = params;

      // Use a default WeatherPreferences object for now
      const defaultPreferences: WeatherPreferences = {
        cloudCoverThreshold: 50,
        minTemperature: 0,
        maxWindSpeed: 20,
        precipitationThreshold: 0.2,
        preferredTime: "any",
        notificationEnabled: false,
        notificationLeadTime: 60,
        units: "metric" as const,
        locations: [],
      };

      // Convert parameters to match API expectations
      const data = await simulateViewingConditions(lat, lon, defaultPreferences);

      // Create the properly shaped result object
      const result: ExtendedSimulationResult = {
        ...(data as any),
        timestamp: data.timestamp || new Date().toISOString(),
        id: data.id || `sim_${Date.now()}`,
      };

      // Add location name if provided
      if (locationName && result.location) {
        result.location.name = locationName;
      }

      // Calculate overallScore if not provided by API
      if (!result.overallScore && result.predicted_conditions?.viewing_score) {
        result.overallScore = result.predicted_conditions.viewing_score;
      }

      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Simulation failed",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Define the shape of the save simulation response
interface SaveSimulationResponse {
  id: string;
  name: string;
  timestamp: string;
  savedAt: string;
  location?: {
    name?: string;
    latitude?: number;
    longitude?: number;
  };
  overallScore?: number;
  [key: string]: any;
}

// Save a simulation
export const saveSimulation = createAsyncThunk<
  SaveSimulationResponse,
  string,
  { state: RootState; rejectValue: string }
>(
  "simulation/saveSimulation",
  async (name: string, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const result = state.simulation.result;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      if (!result) {
        return rejectWithValue("No simulation to save");
      }

      const response = await axios.post(
        "/api/simulations",
        {
          name,
          simulationData: result,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return {
        ...response.data,
        savedAt: new Date().toISOString(),
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to save simulation",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Fetch saved simulations list
export const fetchSavedSimulations = createAsyncThunk<
  SavedSimulation[],
  void,
  { state: RootState; rejectValue: string }
>(
  "simulation/fetchSavedSimulations",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await axios.get("/api/simulations", {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch saved simulations",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Fetch a specific saved simulation
export const fetchSavedSimulation = createAsyncThunk<
  ExtendedSimulationResult,
  string,
  { state: RootState; rejectValue: string }
>(
  "simulation/fetchSavedSimulation",
  async (simulationId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const response = await axios.get(`/api/simulations/${simulationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch simulation",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

// Delete a saved simulation
export const deleteSimulation = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: string }
>(
  "simulation/deleteSimulation",
  async (simulationId: string, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      await axios.delete(`/api/simulations/${simulationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      return simulationId;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to delete simulation",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

const simulationSlice = createSlice({
  name: "simulation",
  initialState,
  reducers: {
    clearSimulation(state) {
      state.result = null;
      state.error = null;
    },
    clearSelectedSavedSimulation(state) {
      state.selectedSavedSimulation = null;
      state.loadingSavedSimulationError = null;
    },
    clearErrors(state) {
      state.error = null;
      state.savingError = null;
      state.loadingSavedListError = null;
      state.loadingSavedSimulationError = null;
    },
    updateSimulationLocation(
      state,
      action: PayloadAction<{
        latitude: number;
        longitude: number;
        name?: string;
      }>,
    ) {
      if (state.result) {
        if (!state.result.location) {
          state.result.location = {};
        }

        state.result.location = {
          ...state.result.location,
          ...action.payload,
        };
      }
    },
    // Add a new action to update viewing scores manually if needed
    updateViewingScores(
      state,
      action: PayloadAction<{
        overall?: number;
        satelliteScores?: Record<string, number>;
      }>,
    ) {
      if (state.result) {
        if (action.payload.overall !== undefined) {
          state.result.overallScore = action.payload.overall;
          if (state.result.predicted_conditions) {
            state.result.predicted_conditions.viewing_score =
              action.payload.overall;
          }
        }

        // Check if both satelliteScores and satellites exist before proceeding
        if (action.payload.satelliteScores && state.result.satellites) {
          state.result.satellites = state.result.satellites.map(
            (sat: Satellite) => {
              // Make sure sat.id exists and is in the satelliteScores object
              if (
                sat.id !== undefined &&
                action.payload.satelliteScores &&
                sat.id.toString() in action.payload.satelliteScores
              ) {
                return {
                  ...sat,
                  viewingScore:
                    action.payload.satelliteScores[sat.id.toString()],
                };
              }
              return sat;
            },
          );
        }
      }
    },
  },
  extraReducers: (builder) => {
    // Handle simulateConditions
    builder
      .addCase(simulateConditions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(simulateConditions.fulfilled, (state, action) => {
        state.loading = false;
        state.result = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(simulateConditions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Handle saveSimulation
    builder
      .addCase(saveSimulation.pending, (state) => {
        state.savingSimulation = true;
        state.savingError = null;
      })
      .addCase(saveSimulation.fulfilled, (state, action) => {
        state.savingSimulation = false;
        if (state.result) {
          // These properties are now explicitly defined in ExtendedSimulationResult
          state.result.savedAt = action.payload.savedAt;
          state.result.id = action.payload.id;
        }

        // Add to saved simulations list if not already there
        const exists = state.savedSimulations.some(
          (sim: SavedSimulation) => sim.id === action.payload.id,
        );
        if (!exists) {
          const viewingScore =
            state.result?.predicted_conditions?.viewing_score ||
            state.result?.overallScore ||
            0;

          // Make sure location exists
          const locationObj = state.result?.location || {};

          state.savedSimulations.push({
            id: action.payload.id,
            name: action.payload.name,
            timestamp: action.payload.timestamp,
            location:
              locationObj.name ||
              `${locationObj.latitude || 0}, ${locationObj.longitude || 0}`,
            overallScore: action.payload.overallScore,
            viewing_score: viewingScore,
          });
        }
      })
      .addCase(saveSimulation.rejected, (state, action) => {
        state.savingSimulation = false;
        state.savingError = action.payload as string;
      });

    // Handle fetchSavedSimulations
    builder
      .addCase(fetchSavedSimulations.pending, (state) => {
        state.loadingSavedList = true;
        state.loadingSavedListError = null;
      })
      .addCase(fetchSavedSimulations.fulfilled, (state, action) => {
        state.loadingSavedList = false;
        state.savedSimulations = action.payload;
      })
      .addCase(fetchSavedSimulations.rejected, (state, action) => {
        state.loadingSavedList = false;
        state.loadingSavedListError = action.payload as string;
      });

    // Handle fetchSavedSimulation
    builder
      .addCase(fetchSavedSimulation.pending, (state) => {
        state.loadingSavedSimulation = true;
        state.loadingSavedSimulationError = null;
      })
      .addCase(fetchSavedSimulation.fulfilled, (state, action) => {
        state.loadingSavedSimulation = false;
        state.selectedSavedSimulation = action.payload;
      })
      .addCase(fetchSavedSimulation.rejected, (state, action) => {
        state.loadingSavedSimulation = false;
        state.loadingSavedSimulationError = action.payload as string;
      });

    // Handle deleteSimulation
    builder.addCase(deleteSimulation.fulfilled, (state, action) => {
      // Remove from saved simulations list
      state.savedSimulations = state.savedSimulations.filter(
        (sim: SavedSimulation) => sim.id !== action.payload,
      );

      // Clear selected simulation if it was the one deleted
      if (state.selectedSavedSimulation?.id === action.payload) {
        state.selectedSavedSimulation = null;
      }

      // Clear result if it was the one deleted
      if (state.result?.id === action.payload) {
        state.result = null;
      }
    });
  },
});

export const {
  clearSimulation,
  clearSelectedSavedSimulation,
  clearErrors,
  updateSimulationLocation,
  updateViewingScores,
} = simulationSlice.actions;

// Selectors
export const selectSimulationResult = (state: RootState) =>
  state.simulation.result;
export const selectSimulationLoading = (state: RootState) =>
  state.simulation.loading;
export const selectSimulationError = (state: RootState) =>
  state.simulation.error;
export const selectSavedSimulations = (state: RootState) =>
  state.simulation.savedSimulations;
export const selectSelectedSavedSimulation = (state: RootState) =>
  state.simulation.selectedSavedSimulation;
export const selectLoadingSavedSimulation = (state: RootState) =>
  state.simulation.loadingSavedSimulation;

export default simulationSlice.reducer;
